import clsx from 'clsx';
import { createElement, ElementType, ReactNode } from 'react';

import { FileName } from '~components/generic/filename/FileName';
import { Spinner } from '~components/generic/spinner/Spinner';
import { DownloadIcon, FolderSymlinkIcon, FolderZipIcon, ThumbnailIcon } from '~components/icons';
import { ConnectionIcon } from '~components/icons/ConnectionIcon';
import useLabels from '~hooks/useLabels';
import styles from './Downloads.module.css';

type DownloadProps = {
  isSharingEnabled?: boolean;
  onShare?: () => void;
  showDownload?: boolean;
  showRaiseRequest?: boolean;
  onRaiseRequest?: () => void;
  filename: string;
  previewUrl?: string;
  onDownload?: () => void;
  downloadLoading?: boolean;
  shareLoading?: boolean;
  size?: string;
  description?: ReactNode;
  as?: ElementType;
  showAddToMyFiles?: boolean;
};

export function Download({
  isSharingEnabled,
  onShare,
  filename,
  showDownload = true,
  onDownload,
  downloadLoading,
  shareLoading,
  showRaiseRequest = false,
  onRaiseRequest,
  previewUrl,
  size,
  description,
  showAddToMyFiles,
  as: Component = 'div',
}: DownloadProps) {
  const { msgNoPreviewAvailable, linkTitleAddMyfiles, linkTitleDownload, linkTitleRaiseRequest } = useLabels([
    'msgNoPreviewAvailable',
    'linkTitleAddMyfiles',
    'linkTitleDownload',
    'linkTitleRaiseRequest',
  ]);

  const content = (
    <div className={clsx(styles.listItemContent, 'downloads-listitem--content')}>
      <div
        className={clsx(styles.thumbnail, 'downloads-listitem--thumbnail')}
        title={!previewUrl ? msgNoPreviewAvailable : undefined}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            className={clsx(styles.thumbnailImg, 'downloads-listitem--thumbnail-img', 'bg-white')}
            alt={`Thumbnail ${filename}`}
          />
        ) : filename.toLowerCase().endsWith('zip') ? (
          <FolderZipIcon />
        ) : (
          <ThumbnailIcon />
        )}
      </div>

      <div className={clsx(styles.name, 'downloads-listitem--name')}>
        <FileName filename={filename} characters={30} />
      </div>

      {!!description && (
        <div className={clsx(styles.description, 'downloads-listitem--description')}>{description}</div>
      )}

      <div className={clsx(styles.size, 'downloads-listitem--size')}>{size ? size : <span>-</span>}</div>

      {!!isSharingEnabled && showAddToMyFiles && (
        <div
          className={clsx(styles.button, 'downloads-listitem--button')}
          title={linkTitleAddMyfiles}
          onClick={() => onShare?.()}
        >
          {shareLoading ? (
            <Spinner className={clsx(styles.buttonSpinner, 'downloads-listitem--button-spinner')} />
          ) : (
            <div className={clsx(styles.buttonIcon, 'downloads-listitem--button-icon')}>
              <FolderSymlinkIcon />
            </div>
          )}
        </div>
      )}

      {!!showDownload && (
        <div
          className={clsx(styles.button, 'downloads-listitem--button')}
          title={linkTitleDownload}
          onClick={() => onDownload?.()}
        >
          {downloadLoading ? (
            <Spinner className={clsx(styles.buttonSpinner, 'downloads-listitem--button-spinner')} />
          ) : (
            <div className={clsx(styles.buttonIcon, 'downloads-listitem--button-icon')}>
              <DownloadIcon />
            </div>
          )}
        </div>
      )}

      {!!showRaiseRequest && (
        <div
          className={clsx(styles.button, 'downloads-listitem--button')}
          title={linkTitleRaiseRequest}
          onClick={() => onRaiseRequest?.()}
        >
          <div className={clsx(styles.buttonIcon, 'downloads-listitem--button-icon')}>
            <ConnectionIcon className="w-5 h-5 mr-2" />
          </div>
        </div>
      )}
    </div>
  );

  return createElement(
    Component,
    {
      className: clsx(styles.listItem, 'downloads-listitem'),
    },
    content,
  );
}
